import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
    Button,
    Modal,
    ModalBody,
    Input,
    Label,
    Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import {
    fetchUserSystemTempClientAccess,
    updateUserSystemTempClientAccess,
} from "model/UserModel";
import "./client-access-add-edit-modal.scss";

const normalizeClientId = (value) => String(value ?? "").trim();

const isSameClientId = (left, right) =>
    normalizeClientId(left) === normalizeClientId(right);

const normalizeClientRow = (client = {}) => ({
    clientId: normalizeClientId(client?.clientId),
    clientName: String(client?.clientName ?? "").trim(),
    keyName: String(client?.keyName ?? client?.keyname ?? client?.clientId ?? "").trim(),
    isAssigned: Boolean(client?.isAssigned),
});

const buildClientsKeyName = (clients = []) =>
    [...(Array.isArray(clients) ? clients : [])]
        .sort((left, right) =>
            normalizeClientId(left?.clientId).localeCompare(
                normalizeClientId(right?.clientId)
            )
        )
        .map((client) => `${client?.clientId}=${client?.isAssigned ? 1 : 0}`)
        .join("|");

const buildSavePayload = (userId, clients = []) => ({
    userId: String(userId ?? "").trim(),
    clientIds: (Array.isArray(clients) ? clients : [])
        .filter((client) => client?.isAssigned)
        .map((client) => normalizeClientId(client?.clientId))
        .filter(Boolean),
});

const TemporaryClientAccessAddEditModal = ({
    tempClientAccessModalOpen,
    setTempClientAccessModalOpen,
    userRow = null,
}) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [userId, setUserId] = useState("");
    const [userName, setUserName] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [clients, setClients] = useState([]);
    const [clientsKeyName, setClientsKeyName] = useState("");

    const resolvedUserId = String(
        userRow?.id ?? userRow?._id ?? userRow?.userId ?? userId ?? ""
    ).trim();

    const assignedClientsKeyName = useMemo(
        () => buildClientsKeyName(clients),
        [clients]
    );

    useEffect(() => {
        if (!tempClientAccessModalOpen) return;

        const rowId = String(
            userRow?.id ?? userRow?._id ?? userRow?.userId ?? ""
        ).trim();
        if (!rowId) {
            toast.error("User id is missing");
            return;
        }

        let cancelled = false;

        const loadTempClientAccess = async () => {
            setLoading(true);
            try {
                const response = await fetchUserSystemTempClientAccess(rowId);
                if (cancelled) return;

                const data = response?.data ?? response ?? {};
                const clientRows = Array.isArray(data?.clients) ? data.clients : [];
                const normalizedClients = clientRows.map(normalizeClientRow);

                setUserId(String(data?.userId ?? rowId).trim());
                setUserName(
                    String(
                        data?.userName ??
                            userRow?.name ??
                            userRow?.userName ??
                            ""
                    ).trim()
                );
                setUserEmail(String(userRow?.email ?? "").trim());
                setClients(normalizedClients);
                setClientsKeyName(buildClientsKeyName(normalizedClients));
            } catch {
                if (!cancelled) {
                    setClients([]);
                    setClientsKeyName("");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadTempClientAccess();

        return () => {
            cancelled = true;
        };
    }, [tempClientAccessModalOpen, userRow]);

    const closeModal = () => {
        setTempClientAccessModalOpen(false);
    };

    const handleClientAssignedChange = (clientId, checked) => {
        setClients((prev) =>
            (prev || []).map((client) =>
                isSameClientId(client?.clientId, clientId)
                    ? { ...client, isAssigned: checked }
                    : client
            )
        );
    };

    const handleSave = async () => {
        if (!resolvedUserId) {
            toast.error("User id is missing");
            return;
        }

        setSaving(true);
        try {
            const payload = buildSavePayload(resolvedUserId, clients);
            const response = await updateUserSystemTempClientAccess(
                resolvedUserId,
                payload
            );

            if (response?.success) {
                toast.success(
                    response?.message || "Temporary client access updated successfully"
                );
                closeModal();
                return;
            }

            toast.error(response?.message || "Unable to update temporary client access");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            isOpen={tempClientAccessModalOpen}
            toggle={closeModal}
            backdrop="static"
            size="lg"
            className="modal-full-height client-access-add-edit-modal"
            centered
        >
            <ModalBody className="modal-height-fixed overflow-y-auto">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="fw-bold m-0 text-uppercase">Temporary Client Access</h5>
                    <Button close onClick={closeModal} />
                </div>
                <hr />

                {loading ? (
                    <div className="d-flex justify-content-center py-5">
                        <Spinner color="primary" />
                    </div>
                ) : (
                    <>
                        <div className="client-access-user-card mb-3">
                            <div className="client-access-user-field">
                                <div className="client-access-user-field__label">
                                    <i className="fa fa-envelope-o" aria-hidden="true" />
                                    Email
                                </div>
                                <div className="client-access-user-field__value">
                                    {userEmail || "—"}
                                </div>
                            </div>
                            <div className="client-access-user-field">
                                <div className="client-access-user-field__label">
                                    <i className="fa fa-user" aria-hidden="true" />
                                    Name
                                </div>
                                <div className="client-access-user-field__value">
                                    {userName || "—"}
                                </div>
                            </div>
                        </div>

                        <Label className="fw-semibold mb-2">Client</Label>
                        <div className="client-access-list border rounded">
                            {(clients || []).length ? (
                                (clients || []).map((client, index) => {
                                    const rowKeyName = String(
                                        client?.keyName ?? client?.clientId ?? index
                                    ).trim();

                                    return (
                                        <div
                                            key={`temp-client-access-row-${rowKeyName}`}
                                            className={`client-access-row d-flex align-items-center gap-3 px-3 py-2 ${
                                                index % 2 === 0
                                                    ? "client-access-row-even"
                                                    : "client-access-row-odd"
                                            }`}
                                        >
                                            <Input
                                                key={`temp-client-access-assigned-${rowKeyName}-${assignedClientsKeyName}-${clientsKeyName}`}
                                                type="checkbox"
                                                className="form-check-input m-0 flex-shrink-0"
                                                defaultChecked={Boolean(client?.isAssigned)}
                                                onChange={(event) =>
                                                    handleClientAssignedChange(
                                                        client?.clientId,
                                                        event.target.checked
                                                    )
                                                }
                                            />
                                            <span className="flex-grow-1">
                                                {client?.clientName || `Client ${client?.clientId}`}
                                            </span>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-muted text-center py-4">
                                    No clients found
                                </div>
                            )}
                        </div>

                        <div className="text-end mt-4">
                            <Button color="primary" onClick={handleSave} disabled={saving}>
                                {saving ? <Spinner size="sm" className="me-1" /> : null}
                                Save
                            </Button>
                        </div>
                    </>
                )}
            </ModalBody>
        </Modal>
    );
};

TemporaryClientAccessAddEditModal.propTypes = {
    tempClientAccessModalOpen: PropTypes.bool,
    setTempClientAccessModalOpen: PropTypes.func,
    userRow: PropTypes.object,
};

export default TemporaryClientAccessAddEditModal;
